import { Archive, ChevronRight, Download, FileSpreadsheet, LoaderCircle, MapPin, Truck, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { MasterImportWizardDialog } from "../components/imports/MasterImportWizardDialog";
import { masterImportConfigs } from "../components/imports/masterImportConfigs";
import { MileageSection } from "../components/settings/MileageSection";
import { NumberSeriesSection } from "../components/settings/NumberSeriesSection";
import { downloadResource, extractErrorMessage, fetchSettings, updateResource } from "../lib/api";

const exportDatasets = [
  { key: "loads", label: "Loads" },
  { key: "dispatch_board", label: "Dispatch Board" },
  { key: "customers", label: "Customers" },
  { key: "drivers", label: "Drivers" },
  { key: "trucks", label: "Trucks" },
  { key: "trailers", label: "Trailers" },
  { key: "invoices", label: "Invoices" },
];

export const SettingsPage = () => {
  const [settings, setSettings] = useState({ company_name: "", company_address: "", company_phone: "", company_email: "", default_invoice_prefix: "INV", default_currency: "CAD", distance_unit: "Miles" });
  const [saving, setSaving] = useState(false);
  const [exportSelection, setExportSelection] = useState({ loads: true, dispatch_board: false, customers: false, drivers: false, trucks: false, trailers: false, invoices: false });
  const [exporting, setExporting] = useState(false);
  const [activeImportKey, setActiveImportKey] = useState(null);
  const [backingUp, setBackingUp] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const payload = await fetchSettings();
        setSettings(payload);
      } catch (error) {
        toast.error(extractErrorMessage(error, "Unable to load settings."));
      }
    };
    loadSettings();
  }, []);

  const handleChange = (event) => setSettings((current) => ({ ...current, [event.target.name]: event.target.value }));

  const selectedDatasets = useMemo(() => exportDatasets.filter((item) => exportSelection[item.key]).map((item) => item.key), [exportSelection]);

  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await updateResource("/settings", "", settings);
      toast.success("Settings updated.");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to save settings."));
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    if (!selectedDatasets.length) {
      toast.error("Select at least one dataset to export.");
      return;
    }
    try {
      setExporting(true);
      for (const dataset of selectedDatasets) {
        const file = await downloadResource("/exports/csv", { dataset });
        triggerDownload(file.blob, file.filename);
      }
      toast.success(`Prepared ${selectedDatasets.length} CSV export${selectedDatasets.length > 1 ? "s" : ""}.`);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to export CSV data."));
    } finally {
      setExporting(false);
    }
  };

  const activeImportConfig = activeImportKey ? masterImportConfigs[activeImportKey] : null;

  const handleBackupDownload = async () => {
    try {
      setBackingUp(true);
      const file = await downloadResource("/backup/export");
      triggerDownload(file.blob, file.filename);
      toast.success("Backup zip downloaded.");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to download the backup."));
    } finally {
      setBackingUp(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <section>
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Company defaults</p>
        <h1 className="mt-2 text-4xl font-semibold text-zinc-900">Settings</h1>
      </section>
      <form className="panel-surface grid gap-4 px-6 py-6 md:grid-cols-2" data-testid="settings-form" onSubmit={handleSubmit}>
        <label className="block space-y-2"><span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Company name</span><input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="settings-company-name-input" name="company_name" onChange={handleChange} value={settings.company_name} /></label>
        <label className="block space-y-2"><span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Company phone</span><input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="settings-company-phone-input" name="company_phone" onChange={handleChange} value={settings.company_phone} /></label>
        <label className="block space-y-2"><span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Company email</span><input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="settings-company-email-input" name="company_email" onChange={handleChange} value={settings.company_email} /></label>
        <label className="block space-y-2"><span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Invoice prefix</span><input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="settings-invoice-prefix-input" name="default_invoice_prefix" onChange={handleChange} value={settings.default_invoice_prefix} /></label>
        <label className="block space-y-2 md:col-span-2"><span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Company address</span><textarea className="min-h-[120px] w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="settings-company-address-input" name="company_address" onChange={handleChange} value={settings.company_address} /></label>
        <label className="block space-y-2"><span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Default currency</span><select className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="settings-default-currency-select" name="default_currency" onChange={handleChange} value={settings.default_currency}><option value="CAD">CAD</option><option value="USD">USD</option></select></label>
        <label className="block space-y-2"><span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Distance unit</span><select className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="settings-distance-unit-select" name="distance_unit" onChange={handleChange} value={settings.distance_unit}><option value="Miles">Miles</option><option value="Kilometers">Kilometers</option></select></label>
        <div className="flex justify-end md:col-span-2"><button className="rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white" data-testid="settings-save-button" disabled={saving} type="submit">{saving ? "Saving..." : "Save settings"}</button></div>
      </form>

      <NumberSeriesSection />

      <MileageSection />

      <section className="panel-surface px-6 py-6" data-testid="settings-backup-section">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Disaster recovery</p>
          <h2 className="mt-2 text-3xl font-semibold text-zinc-900">Full data backup</h2>
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-between gap-4 rounded-[28px] border border-zinc-200 bg-zinc-50 px-5 py-5">
          <div className="flex items-center gap-3">
            <div className="rounded-2xl bg-white p-3 text-zinc-700"><Archive className="h-5 w-5" /></div>
            <div>
              <p className="text-sm font-semibold text-zinc-900">Download a complete backup (.zip)</p>
              <p className="text-sm text-zinc-500">Every table — loads, invoices, customers, equipment, drivers, users and more — as JSON + CSV, with a manifest.</p>
            </div>
          </div>
          <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-zinc-400" data-testid="settings-download-backup-button" disabled={backingUp} onClick={handleBackupDownload} type="button">
            {backingUp ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {backingUp ? "Preparing backup..." : "Download backup (.zip)"}
          </button>
        </div>
      </section>

      <section className="panel-surface px-6 py-6" data-testid="settings-import-export-section">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Import / Export</p>
          <h2 className="mt-2 text-3xl font-semibold text-zinc-900">CSV tools</h2>
        </div>

        <div className="mt-6 grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
          <div className="space-y-6">
            <div className="rounded-[32px] border border-amber-200 bg-[linear-gradient(135deg,#fff7ed_0%,#ffffff_45%,#fef3c7_100%)] px-5 py-5" data-testid="settings-load-import-card">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">Load imports</p>
              <h3 className="mt-2 text-2xl font-semibold text-zinc-900">Import loads</h3>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white" data-testid="settings-open-load-import-link" to="/loads/import">
                  <FileSpreadsheet className="h-4 w-4" /> Open load import
                </Link>
                <a className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-5 py-3 text-sm font-semibold text-zinc-700" data-testid="settings-download-load-template-link" href="/load-import-template.csv?v=customer-ref">
                  <Download className="h-4 w-4" /> Download load template
                </a>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-[28px] border border-zinc-200 bg-zinc-50 px-5 py-5" data-testid="settings-driver-import-card">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Master data</p>
                    <h3 className="mt-2 text-2xl font-semibold text-zinc-900">Drivers</h3>
                  </div>
                  <div className="rounded-2xl bg-white p-3 text-zinc-700"><Users className="h-5 w-5" /></div>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white" data-testid="settings-open-driver-import-button" onClick={() => setActiveImportKey("drivers")} type="button">
                    <FileSpreadsheet className="h-4 w-4" /> Import drivers
                  </button>
                  <a className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-5 py-3 text-sm font-semibold text-zinc-700" data-testid="settings-download-driver-template-link" href={masterImportConfigs.drivers.templatePath}>
                    <Download className="h-4 w-4" /> Template
                  </a>
                </div>
              </div>

              <div className="rounded-[28px] border border-zinc-200 bg-zinc-50 px-5 py-5" data-testid="settings-customer-import-card">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Master data</p>
                    <h3 className="mt-2 text-2xl font-semibold text-zinc-900">Customers</h3>
                  </div>
                  <div className="rounded-2xl bg-white p-3 text-zinc-700"><Users className="h-5 w-5" /></div>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white" data-testid="settings-open-customer-import-button" onClick={() => setActiveImportKey("customers")} type="button">
                    <FileSpreadsheet className="h-4 w-4" /> Import customers
                  </button>
                  <a className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-5 py-3 text-sm font-semibold text-zinc-700" data-testid="settings-download-customer-template-link" href={masterImportConfigs.customers.templatePath}>
                    <Download className="h-4 w-4" /> Template
                  </a>
                </div>
              </div>

              <div className="rounded-[28px] border border-zinc-200 bg-zinc-50 px-5 py-5 lg:col-span-2" data-testid="settings-equipment-import-card">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Equipment</p>
                    <h3 className="mt-2 text-2xl font-semibold text-zinc-900">Trucks and trailers</h3>
                  </div>
                  <div className="rounded-2xl bg-white p-3 text-zinc-700"><Truck className="h-5 w-5" /></div>
                </div>
                <div className="mt-5 grid gap-3 md:grid-cols-2">
                  <div className="rounded-2xl border border-zinc-200 bg-white px-4 py-4" data-testid="settings-truck-import-action-card">
                    <p className="text-sm font-semibold text-zinc-900">Truck CSV</p>
                    <div className="mt-4 flex flex-wrap gap-3">
                      <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-4 py-2.5 text-sm font-semibold text-white" data-testid="settings-open-truck-import-button" onClick={() => setActiveImportKey("trucks")} type="button">
                        <ChevronRight className="h-4 w-4" /> Import trucks
                      </button>
                      <a className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-2.5 text-sm font-semibold text-zinc-700" data-testid="settings-download-truck-template-link" href={masterImportConfigs.trucks.templatePath}>
                        <Download className="h-4 w-4" /> Template
                      </a>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-zinc-200 bg-white px-4 py-4" data-testid="settings-trailer-import-action-card">
                    <p className="text-sm font-semibold text-zinc-900">Trailer CSV</p>
                    <div className="mt-4 flex flex-wrap gap-3">
                      <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-4 py-2.5 text-sm font-semibold text-white" data-testid="settings-open-trailer-import-button" onClick={() => setActiveImportKey("trailers")} type="button">
                        <ChevronRight className="h-4 w-4" /> Import trailers
                      </button>
                      <a className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-2.5 text-sm font-semibold text-zinc-700" data-testid="settings-download-trailer-template-link" href={masterImportConfigs.trailers.templatePath}>
                        <Download className="h-4 w-4" /> Template
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-[28px] border border-zinc-200 bg-zinc-50 px-5 py-5 lg:col-span-2" data-testid="settings-location-import-card">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Master data</p>
                    <h3 className="mt-2 text-2xl font-semibold text-zinc-900">Locations</h3>
                  </div>
                  <div className="rounded-2xl bg-white p-3 text-zinc-700"><MapPin className="h-5 w-5" /></div>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white" data-testid="settings-open-location-import-button" onClick={() => setActiveImportKey("locations")} type="button">
                    <FileSpreadsheet className="h-4 w-4" /> Import locations
                  </button>
                  <a className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-5 py-3 text-sm font-semibold text-zinc-700" data-testid="settings-download-location-template-link" href={masterImportConfigs.locations.templatePath}>
                    <Download className="h-4 w-4" /> Template
                  </a>
                </div>
              </div>
            </div>

            <div className="rounded-[28px] border border-dashed border-zinc-300 bg-white px-5 py-5" data-testid="settings-load-history-import-note-card">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Planned next</p>
              <h3 className="mt-2 text-2xl font-semibold text-zinc-900">Completed loads / loads history</h3>
            </div>
          </div>

          <div className="rounded-3xl border border-zinc-200 bg-zinc-50 px-5 py-5" data-testid="settings-export-card">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Export</p>
            <h3 className="mt-2 text-2xl font-semibold text-zinc-900">Selected CSV exports</h3>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {exportDatasets.map((dataset) => (
                <label className="flex items-start gap-3 rounded-2xl border border-zinc-200 bg-white px-4 py-4" key={dataset.key}>
                  <input checked={Boolean(exportSelection[dataset.key])} data-testid={`settings-export-${dataset.key}-checkbox`} onChange={() => setExportSelection((current) => ({ ...current, [dataset.key]: !current[dataset.key] }))} type="checkbox" />
                  <span className="text-sm text-zinc-700">{dataset.label}</span>
                </label>
              ))}
            </div>
            <button className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-zinc-400" data-testid="settings-export-selected-button" disabled={exporting} onClick={handleExport} type="button">
              {exporting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export selected CSV files
            </button>
          </div>
        </div>
      </section>

      <MasterImportWizardDialog config={activeImportConfig} onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setActiveImportKey(null);
        }
      }} open={Boolean(activeImportConfig)} />
    </div>
  );
};